import { Button, Modal, Radio, Space } from 'antd';
import { useEffect, useState } from 'react';

import { CALENDAR_SERIES_SCOPE } from 'app/typings/core/extranet/inventory/enums/CalendarSeriesScope';
import type { CalendarSeriesScope } from 'app/typings/core/extranet/inventory/enums/CalendarSeriesScope';

import styles from './ApplyChangesModal.module.scss';

type ApplyChangesMode = 'edit' | 'delete';

const SCOPE_LABELS: Record<
  ApplyChangesMode,
  Record<CalendarSeriesScope, string>
> = {
  edit: {
    THIS: 'This timeslot only',
    THIS_AND_NEXT: 'This and all upcoming timeslots',
    ALL: 'All timeslots in the series',
  },
  delete: {
    THIS: 'Delete this timeslot only',
    THIS_AND_NEXT: 'Delete all upcoming timeslots from today',
    ALL: 'Delete all timeslots in the series (past and future)',
  },
};

const TITLES: Record<ApplyChangesMode, string> = {
  edit: 'Apply your changes to…',
  delete: 'Delete timeslot',
};

const QUESTIONS: Record<ApplyChangesMode, string> = {
  edit: 'What do you want to edit?',
  delete: 'What do you want to delete?',
};

const ALL_SCOPES: CalendarSeriesScope[] = [
  CALENDAR_SERIES_SCOPE.THIS,
  CALENDAR_SERIES_SCOPE.THIS_AND_NEXT,
  CALENDAR_SERIES_SCOPE.ALL,
];

type Props = {
  isOpen: boolean;
  isLoading?: boolean;
  mode?: ApplyChangesMode;
  scopes?: CalendarSeriesScope[];
  onCancel: () => void;
  onConfirm: (scope: CalendarSeriesScope) => void;
};

export const ApplyChangesModal = ({
  isOpen,
  isLoading,
  mode = 'edit',
  scopes = ALL_SCOPES,
  onCancel,
  onConfirm,
}: Props) => {
  const [scope, setScope] = useState<CalendarSeriesScope>(scopes[0]);

  useEffect(() => {
    if (isOpen) {
      setScope(scopes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset au scope par défaut à chaque ouverture, sans réagir aux changements de `scopes`
  }, [isOpen]);

  return (
    <Modal
      className={styles.modal}
      wrapClassName={styles.applyChangesModal}
      width={572}
      open={isOpen}
      title={TITLES[mode]}
      onCancel={onCancel}
      footer={[
        <Button key="back" type="ghost" onClick={onCancel}>
          Back to edit
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={() => onConfirm(scope)}
          loading={isLoading}
        >
          Confirm
        </Button>,
      ]}
    >
      <p>
        This slot is part of a series.{' '}
        <span className={styles.bold}>{QUESTIONS[mode]}</span>
      </p>
      <Radio.Group
        value={scope}
        onChange={(event) =>
          setScope(event.target.value as CalendarSeriesScope)
        }
      >
        <Space direction="vertical" size={12}>
          {scopes.map((value) => (
            <Radio key={value} value={value}>
              {SCOPE_LABELS[mode][value]}
            </Radio>
          ))}
        </Space>
      </Radio.Group>
    </Modal>
  );
};
